import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b8d07c5d-fcca-4dc8-ad54-fc1da9664427/artifacts/71fm56iq_LOGO.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/get-in-touch', label: t('nav.getInTouch') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
      }`}
      data-testid="navbar"
    >
      <nav className="container-main">
        <div className="flex items-center justify-between h-20">
          {/* Logo + Brand Name */}
          <Link 
            to="/" 
            className="flex items-center space-x-3"
            data-testid="navbar-logo"
            aria-label="EuroFortaleza Home"
          >
            <img 
              src={LOGO_URL} 
              alt="EuroFortaleza Construction Logo" 
              className="h-12 w-auto"
            />
            <span className="font-alatsi text-lg sm:text-xl uppercase tracking-wider" style={{ color: '#051436' }}>
              EUROFORTALEZA
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-garet text-sm uppercase tracking-widest transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-gold-main font-semibold'
                    : 'text-navy-deep hover:text-gold-main'
                }`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: Language + CTA */}
          <div className="hidden lg:flex items-center space-x-6">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-navy-deep hover:text-gold-main transition-colors"
              data-testid="language-switcher"
              aria-label={`Switch to ${language === 'en' ? 'Portuguese' : 'English'}`}
            >
              <Globe className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase">{language === 'en' ? 'PT' : 'EN'}</span>
            </button>

            {/* CTA Button */}
            <Link
              to="/get-in-touch"
              className="bg-navy-deep text-white hover:bg-gold-main transition-colors duration-300 px-6 py-3 text-sm uppercase tracking-widest font-semibold"
              data-testid="navbar-cta"
            >
              {t('hero.cta')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 text-navy-deep"
              data-testid="mobile-language-switcher"
              aria-label={`Switch to ${language === 'en' ? 'Portuguese' : 'English'}`}
            >
              <Globe className="w-5 h-5" />
              <span className="text-xs font-semibold">{language === 'en' ? 'PT' : 'EN'}</span>
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-navy-deep p-2"
              data-testid="mobile-menu-toggle"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-6 space-y-4 border-t border-gray-100">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block py-3 font-garet text-base uppercase tracking-wide ${
                      isActive(link.path)
                        ? 'text-gold-main font-semibold'
                        : 'text-navy-deep'
                    }`}
                    data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/get-in-touch"
                  className="block w-full bg-navy-deep text-white text-center py-4 mt-4 uppercase tracking-widest font-semibold"
                  data-testid="mobile-navbar-cta"
                >
                  {t('hero.cta')}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Navbar;
